/**
 * Created by Ning Jiang on 2021-05-06.
 */

import {gsap, TimelineLite} from "gsap";
import {IReelStateEventData, ReelState} from "../../core/reel/ReelState";
import {OktoberfestSymbol} from "../symbol/OktoberfestSymbol";
import {OktoberfestReelView} from "./OktoberfestReelView";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {MathHelper} from "../../core/utils/MathHelper";
import {IParsedStackedSymbolName, StackedSymbol} from "../../core/reel/stackedsymbol/StackedSymbol";
import {Oktoberfest} from "../Oktoberfest";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {OktoberfestGameConfig} from "../OktoberfestGameConfig";
import {OktoberfestSymbolConfig, OktoberfestSymbolState} from "../symbol/OktoberfestSymbolConfig";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {OktoberfestStackedSymbol} from "../symbol/OktoberfestStackedSymbol";
import {OktoberfestScatter} from "../symbol/OktoberfestScatter";

export class OktoberfestReelController {
    public static readonly symbolsNumInData: number[] = [0, 2, 3, 2, 0]; // see legacy doc wiki about reel config.
    public readonly events: APIEventSystem;
    public reelId: number;
    public reelView: OktoberfestReelView;
    public isQuickStop: boolean;
    private _symsNumTotal: number;
    private _currentReelSet: string[]; //The reelSet currently in use, it might be different than the actually reelSet because its made of many different parts.
    private _currentReelSetPosition: number; // The current position in the reel
    private _currentFullReelSet: string[]; // The full actually reelSet that the most part of the _currentReelSet is make of.
    private _stopReelSetFilledBottomNum: number = 0; // Use to calculate added symbols under the server reel symbols when stopping.
    private _extraStopDelay: number;
    private _reelStopDelayTimer: TweenLite | null; //Delay for reel stop
    private _stopCounter: number = 0;

    private _normalStartStepDuration: number = 0.3;
    private _fastStartStepDuration: number = 0.2;
    private _startEase: string = `back.in(4)`;
    private _defaultSpinStepDuration: number = 0.04;
    private _normalStopStepDuration: number = 0.5;
    private _fastStopStepDuration: number = 0.4;
    private _normalStopEase: string = `elastic.out(1.5, 0.95)`;
    private _fastStopEase: string = `elastic.out(1.2, 0.75)`;

    private _abortNudge: boolean;
    private _nudgeTimeline: TimelineLite;
    private _removedSymbolPool: OktoberfestSymbol[] = [];
    private _maxRemovedSymbolPoolLength: number = 3;
    private _isPartySpin: boolean = false;


    constructor(reelId: number) {
        this.events = SlotGame.apiPlugin.eventSystemFactory.create();
        this.events.on("ReelStateChange", (data: IReelStateEventData) => this.events.trigger(data.newState, data)); //To prevent event system from saving once callbacks if no on callbacks is set.

        this.reelId = reelId;
        this.reelState = ReelState.IDLE;
        this._symsNumTotal = ArrayHelper.arraySum(OktoberfestReelController.symbolsNumInData);
        this.reelView = new OktoberfestReelView(reelId);
        this.initSymbols();
    }

    private _reelState: ReelState;

    get reelState(): ReelState {
        return this._reelState;
    }

    set reelState(value: ReelState) {
        if (this.reelState == "inactive") {
            if (value == "active") {
                this._reelState = value;
            }
            return;
        }
        if (value === this._reelState) {
            return;
        }

        const oldState = this._reelState;
        this._reelState = value;
        this.events.trigger("ReelStateChange", <IReelStateEventData>{
            reelId: this.reelId,
            newState: this._reelState,
            oldState: oldState
        });
    }

    public createSymbol(symName: string, reelId: number, state: OktoberfestSymbolState): OktoberfestSymbol {
        switch (symName) {
            case "S":
                return new OktoberfestScatter(symName, this.reelId, state);
            default:
                if (symName.includes("W1") || symName.includes("W2")) {

                    return new OktoberfestStackedSymbol(symName, this.reelId, state);
                }
                return new OktoberfestSymbol(symName, this.reelId, state);
        }
    }

    public startSpin(active: boolean): TimelineLite {
        this._extraStopDelay = 0;
        this._stopCounter = 0;
        this.isQuickStop = false;
        this.reelView.watchBounce = false;

        if (this.reelState == ReelState.INACTIVE) {
            return gsap.timeline();
        } else {
            this.setNextState(
                ReelState.START,
                Oktoberfest.apiPlugin.settings.get(APISetting.FAST_SPIN) ? this._fastStartStepDuration : this._normalStartStepDuration,
                this._startEase
            );
            const tl = gsap.timeline({
                onComplete: () => {
                    if (active) {
                        this.setNextState(ReelState.SPIN, this._defaultSpinStepDuration, 'none');
                        this.spinStep();
                    }
                }
            });
            tl.add(this.reelView.step(this.addNextNewSymbol(OktoberfestSymbolState.SPIN), () => this.removeBottomSymbol()));
            return tl;
        }
    }

    public spinStep(): TimelineLite {
        const tl = gsap.timeline({
            onComplete: () => {
                if (this.reelState === ReelState.SPIN) {
                    this.spinStep();
                    return;
                }
                if (this.reelState === ReelState.STOPPING) {
                    if (this._stopCounter > 1) {
                        this._stopCounter--;
                        this.spinStep();
                        return;
                    }

                    this._stopCounter = 0;
                    this.stopStep();
                }
            }
        });
        tl.add(this.reelView.step(this.addNextNewSymbol(OktoberfestSymbolState.SPIN), () => this.removeBottomSymbol()));
        return tl;
    }

    public stopStep(): TimelineLite {

        this.setNextState(
            ReelState.STOPPING,
            (Oktoberfest.apiPlugin.settings.get(APISetting.FAST_SPIN) || this.isQuickStop) ? this._fastStopStepDuration : this._normalStopStepDuration,
            (Oktoberfest.apiPlugin.settings.get(APISetting.FAST_SPIN) || this.isQuickStop) ? this._fastStopEase : this._normalStopEase
        );
        const tl = gsap.timeline({
            onStart: () => {this.reelView.watchBounce = true},
            onComplete: () => {
                this.reelState = ReelState.STOPPED;
            }
        });
        tl.add(() => {
            Oktoberfest.reelDividers.onReelStop(this.reelId);
        });
        tl.add(this.reelView.step(this.addNextNewSymbol(OktoberfestSymbolState.NORMAL), () => this.removeBottomSymbol()));
        this.reelView.allSymbols.forEach((symbol) => {
            tl.add(() => {
                symbol.getStateAnimation(OktoberfestSymbolState.NORMAL, {duration: 0}, {duration: 0})
            }, 0);
        });
        return tl;
    }

    // The stop offset is for features on the reel to prolong the stop. like if the server gives a half stacked symbol in the bottom, so we need to add more symbols to fill it up.
    public getSpinStopOffset(): number {
        if (this.reelState !== ReelState.SPIN) {
            return 0;
        }

        // check if there is unfinished stacked wilds in the bottom of the stop reel set.
        // When the offset is more than 0, we have to make sure to compensate it from the stop reel set.
        let spinStopOffset: number = 0;
        const reelData: string[] = Oktoberfest.model.reels[this.reelId];
        let bottomSymbol: string = reelData[reelData.length - 1];
        let parsedStackedName: IParsedStackedSymbolName = StackedSymbol.parseStackedSymbolName(bottomSymbol, this.reelId);
        if (parsedStackedName.isStacked && parsedStackedName.index < (parsedStackedName.totalNum - 1)) {
            spinStopOffset = parsedStackedName.totalNum - 1 - parsedStackedName.index;
        }

        return Math.max(0, spinStopOffset - OktoberfestReelController.symbolsNumInData[4]);
    }

    public hasNearWin(): boolean {
        return Oktoberfest.model.parsedGameData.nearWinBigWinReels[this.reelId] || Oktoberfest.model.parsedGameData.nearWinReels[this.reelId];
    }

    public addStopDelay(value: number): void {
        this._extraStopDelay += value;
    }

    public addStopDelayIfNotEnough(value: number): void {
        // Logger.logDev(`reel ${this.reelId} go added delay ${value}, totalDelayBefore ${this._extraStopDelay}`);
        this._extraStopDelay = Math.max(value, this._extraStopDelay);
        // Logger.logDev(`totalDelayAfter ${this._extraStopDelay}`);
    }

    // This is just estimated time
    public getDefaultStoppingTime(): number {
        return (this._symsNumTotal - 1) * this._defaultSpinStepDuration + this._normalStopStepDuration;
    }

    public tryStop(spinStopOffset: number, delay: number, quickStop: boolean, stopStartCallBack: () => void): void {
        // Logger.logDev(`reel ${this.reelId} try to stop in delay ${delay}`);

        this.isQuickStop = quickStop;


        if (this.reelState === ReelState.INACTIVE) {
            // Logger.logDev(`reel ${this.reelId} is inactive, stopped.`);
            stopStartCallBack();
            this.reelState = ReelState.STOPPED;
            return;
        }

        if (this.reelState === ReelState.STOPPING) {
            // Logger.logDev(`reel ${this.reelId} is already stopping.`);
            return;
        }

        if (this._reelStopDelayTimer && this._reelStopDelayTimer.isActive()) {
            this._reelStopDelayTimer.pause();
            this._reelStopDelayTimer.kill();
        }

        if (this.isQuickStop && !this._isPartySpin) {
            this._extraStopDelay = 0;
        }
        const totalDelay: number = delay + this._extraStopDelay;
        this._extraStopDelay = 0;
        // Logger.logDev(`reel ${this.reelId} try to stop in extra delay ${this._extraStopDelay}, total delay ${totalDelay}`);
        if (totalDelay > 0) {
            this._reelStopDelayTimer = gsap.to(this, totalDelay, {onComplete: () => this.tryStop(spinStopOffset, 0, quickStop, stopStartCallBack)});
            return;
        }

        // start stop
        stopStartCallBack();
        this.getReadyToStop(spinStopOffset);

    }

    public isDataSymbolInValidArea(index: number): boolean {
        return index >= OktoberfestReelController.symbolsNumInData[1] && index < (OktoberfestReelController.symbolsNumInData[1] + OktoberfestReelController.symbolsNumInData[2]);
    }

    public getSymbol(index: number, isDataIndex: boolean = false, getVisibleStackedSymbol: boolean = true): OktoberfestSymbol {
        if (!isDataIndex) {
            index += OktoberfestReelController.symbolsNumInData[0] + OktoberfestReelController.symbolsNumInData[1];
        } else {
            index += OktoberfestReelController.symbolsNumInData[0];
        }
        const symbol = this.reelView.allSymbols[index];
        if (!symbol.stackedSymbolName.isStacked) {
            return symbol;
        }
        if (!getVisibleStackedSymbol) {
            return symbol
        }

        // return the most bottom visible symbol of a stacked symbol
        for (let i: number = symbol.stackedSymbolName!.totalNum - 1; i >= 0; i--) {
            const testSymbol = <OktoberfestStackedSymbol>(<OktoberfestStackedSymbol>symbol).stack!.symbols[i];
            if (testSymbol != null && testSymbol.parent != null) {
                return testSymbol;
            }
        }

        debugger;
        return symbol;
    }
    public getStackedSymbolIndex(index:number,isDataIndex:boolean=false):number{
        if (!isDataIndex) {
            index += OktoberfestReelController.symbolsNumInData[0] + OktoberfestReelController.symbolsNumInData[1];
        } else {
            index += OktoberfestReelController.symbolsNumInData[0];
        }
        const symbol = this.reelView.allSymbols[index];
        if (symbol.stackedSymbolName.isStacked) {
            if(symbol.symbolName.length>2){
                let symName =symbol.symbolName;
                return parseInt(symName[symName.length-1],0);
            }
        }
        return 0;
    }
    public nudge(stepsLeft: number,
                 stepDuration: number,
                 ease: string,
                 delay: number,
                 onStepStart: (stepsLeft: number, aborted: boolean) => void,
                 onStepComplete: (stepsLeft: number, aborted: boolean) => void,
                 onReelNudgeComplete: (aborted: boolean) => void,
                 abort?: boolean
    ): TimelineLite {
        if (abort != null) {
            this._abortNudge = abort;
        }
        this.reelState = ReelState.NUDGE;

        const tl = gsap.timeline();
        this._nudgeTimeline = tl;

        // reel nudge complete.
        if (stepsLeft === 0) {
            tl.add(() => {
                onReelNudgeComplete(this._abortNudge);
                this.reelState = ReelState.IDLE;
            }, delay);
            return tl;
        }

        // start nudge
        this.setNextState(ReelState.NUDGE, abort ? 0.001 : stepDuration, ease);
        tl.add(() => onStepStart(stepsLeft, this._abortNudge), delay);

        // Nudge down
        if (stepsLeft > 0) {
            tl.add(this.reelView.step(this.addNextNewSymbol(OktoberfestSymbolState.NORMAL), () => this.removeBottomSymbol()));
            tl.add(() => onStepComplete(stepsLeft - 1, this._abortNudge));
            tl.add(() => {
                this.nudge(stepsLeft - 1,
                    stepDuration,
                    ease,
                    0,
                    onStepStart,
                    onStepComplete,
                    onReelNudgeComplete
                )
            });
            return tl;
        }
        return tl;
    }

    public abortNudge(): void {
        this._abortNudge = true;
        if (this._nudgeTimeline && this._nudgeTimeline.isActive()) {
            this._nudgeTimeline.progress(1);
        }
    }

    public replaceSimpleSymbol(index: number, newSymName: string, state: OktoberfestSymbolState = OktoberfestSymbolState.NORMAL): void {
        const oldSymbol: OktoberfestSymbol = this.getSymbol(index);
        const newSymbol: OktoberfestSymbol = this.createSymbol(newSymName, this.reelId, state);
        newSymbol.position.set(oldSymbol.x, oldSymbol.y);
        this.reelView.addSymbol(newSymbol); // win?
        this.reelView.allSymbols[index + OktoberfestReelController.symbolsNumInData[0] + OktoberfestReelController.symbolsNumInData[1]] = newSymbol;

        this.reelView.removeSymbol(oldSymbol);
        oldSymbol.onRemove();
    }

    // If there are unfinished stacked symbols, fill it up.
    protected handleUnfinishedSpinningStackedSymbols(): string[] {
        const lastStackedName: IParsedStackedSymbolName = this.reelView.allSymbols[0].stackedSymbolName;
        if (!lastStackedName.isStacked || lastStackedName.index === 0) {
            return [];
        }

        return ArrayHelper.initArrayWithValues(lastStackedName.index, index => `${lastStackedName.symName}*${index}`);
    }

    private initSymbols(): void {
        this._currentFullReelSet = this.getReelSet(Oktoberfest.model.parsedInitData.initReelSetName);
        this._currentReelSet = this.makeUpStopReelSet(true);
        if (this._stopReelSetFilledBottomNum < OktoberfestReelController.symbolsNumInData[4]) {
            this._currentReelSet = this._currentReelSet.concat(this.makeUpStopReelSetRandomBottomFillUp(OktoberfestReelController.symbolsNumInData[4] - this._stopReelSetFilledBottomNum));
        }
        this._currentReelSetPosition = this._currentReelSet.length;

        for (let i: number = 0; i < this._symsNumTotal; i++) {
            if (i === 5) { //#610 (top unmasked symbols
                this.addNextNewSymbol(OktoberfestSymbolState.NO_WIN);
            }else if(i==6){
                this.addNextNewSymbol(OktoberfestSymbolState.SPIN);
            } else {
                this.addNextNewSymbol(OktoberfestSymbolState.NORMAL);
            }
        }
        this.reelView.initSymbolsPosition(OktoberfestReelController.symbolsNumInData[0] + OktoberfestReelController.symbolsNumInData[1]);
    }

    private getReelSet(name: string): string[] {
        if (OktoberfestGameConfig.REEL_SETS[name]) {
            const reelSet: string[] = OktoberfestGameConfig.REEL_SETS[name][this.reelId];
            if (reelSet.length < 5) {
                Logger.logDev(`ReelController.getReelSet() name = ${name}, reelId = ${this.reelId}, the reelSet is too short that might break ReelController.randomizeReelSetPosition().`)
            }
            return reelSet;
        }

        debugger;
        throw new Error(`ReelController.getReelSet(): ReelSet ${name} is missing from GameConfig!`);
    }

    // only for spin here
    private makeUpStopReelSet(init: boolean): string[] {
        const stopReelSet = Oktoberfest.model.reels[this.reelId];
        const randomPosition: number = this.randomizeReelSetPosition(this._currentFullReelSet);
        const topFillUp: string[] = this.makeUpStackedSymbolTop(stopReelSet[0]);
        const bottomFillUp: string[] = this.makeUpStackedSymbolBottom(stopReelSet[stopReelSet.length - 1], init);
        // const bottomRandomFillUp:string[] = this.makeUpStopReelSetRandomBottomFillUp(bottomFillUpTotalNum - bottomFillUp.length, init);
        return this._currentFullReelSet.slice(0, randomPosition + 1).concat(topFillUp).concat(stopReelSet).concat(bottomFillUp);
    }

    // Get a position where the symbol is not part of a stacked symbol unless it's the bottom of the stacked symbol.
    private randomizeReelSetPosition(reelSet: string[]): number {
        let position: number = MathHelper.randomNumberInRange(4, reelSet.length - 1); // Start on 10 to make sure there is enough number included.

        const parsedStackedName: IParsedStackedSymbolName = StackedSymbol.parseStackedSymbolName(reelSet[position], this.reelId);
        if (parsedStackedName.isStacked) {
            position += parsedStackedName.totalNum - 1 - parsedStackedName.index;
        }

        return position;
    }

    private makeUpStackedSymbolTop(targetSymName: string): string[] {
        let result: string[] = [];
        const stackedName: IParsedStackedSymbolName = StackedSymbol.parseStackedSymbolName(targetSymName, this.reelId);
        if (stackedName.isStacked && stackedName.index > 0) {
            for (let i: number = 0; i < stackedName.index; i++) {
                result.push(`${stackedName.symName}*${i}`);
            }
        }
        return result;
    }

    private makeUpStackedSymbolBottom(aboveSymName: string, init: boolean): string[] {
        let result: string[] = [];
        const stackedName: IParsedStackedSymbolName = StackedSymbol.parseStackedSymbolName(aboveSymName, this.reelId);
        if (stackedName.isStacked && stackedName.index < (stackedName.totalNum - 1)) {
            let stackedNum: number = init ? Math.min(stackedName.totalNum, stackedName.index + OktoberfestReelController.symbolsNumInData[4] + 1) : stackedName.totalNum;
            for (let i: number = stackedName.index + 1; i < stackedNum; i++) {
                result.push(`${stackedName.symName}*${i}`);
            }
        }
        this._stopReelSetFilledBottomNum = result.length;
        return result;
    }

    private makeUpStopReelSetRandomBottomFillUp(length: number): string[] {
        return ArrayHelper.initArrayWithValues(length, index => this.getRandomSymbolNameFromReelSet(this._currentFullReelSet));
    }

    private getRandomSymbolNameFromReelSet(reelSet: string[]): string {
        let symbolName: string;
        do {
            symbolName = ArrayHelper.getRandomValue<string>(reelSet);
        } while (symbolName.includes("W") || OktoberfestSymbolConfig.SYMBOLS[symbolName].isSpecial);

        return symbolName;
    }

    private addNextNewSymbol(state: OktoberfestSymbolState): OktoberfestSymbol {
        this.moveReelSetPosition();
        const symName: string = this._currentReelSet[this._currentReelSetPosition];
        if (symName == null || symName.length === 0) {
            debugger;
            throw new Error(`ReelController.addNextNewSymbol(): symbol name cannot be empty!`);
        }
        let symbol: OktoberfestSymbol = this.createSymbol(symName, this.reelId, state);
        this.reelView.addSymbol(symbol);
        this.reelView.allSymbols.unshift(symbol);

        const previousSymbol = this.reelView.allSymbols[1];
        StackedSymbol.updateStackedSymbols(symbol, previousSymbol, -1, true);
        if (previousSymbol) {
            symbol.y = previousSymbol.y - previousSymbol.symbolHeight * 0.5 - symbol.bottomPadding - symbol.symbolHeight * 0.5;
        }

        return symbol;
    }

    private removeBottomSymbol(): void {
        const removeSymbol = this.reelView.allSymbols.pop()!;
        StackedSymbol.updateStackedSymbols(this.reelView.allSymbols[this.reelView.allSymbols.length - 1], null, 1, false);
        this.reelView.removeSymbol(removeSymbol);
        this._removedSymbolPool.unshift(removeSymbol);
        if (this._removedSymbolPool.length > this._maxRemovedSymbolPoolLength) {
            this._removedSymbolPool.pop()!.onRemove();
        }
    }

    private moveReelSetPosition(): void {
        if (this._currentReelSetPosition == 0) {
            if (this._currentFullReelSet == null) {
                debugger;
                throw new Error("ReelController.createNextSymbol():This is a bug that Ning is trying to reproduce!");
            }
            this._currentReelSet = this._currentFullReelSet.concat();
        }
        this._currentReelSetPosition--;
        this._currentReelSetPosition = ArrayHelper.reviseIndexInLoopRange(this._currentReelSet.length, this._currentReelSetPosition);
    }

    private setNextState(reelState: ReelState, duration?: number, ease?: string): void {
        this.reelView.nextSpinStepConfig = {
            reelState: reelState,
            duration: duration,
            ease: ease
        }
    }

    private getReadyToStop(spinStopOffset: number): void {
        // Logger.logDev(`reel ${this.reelId} ready to stop.`);
        let addDelayToNextReel = 0;
        const unfinishedSpinningStackedSymbolTopFillUp: string[] = this.handleUnfinishedSpinningStackedSymbols();
        this._currentReelSet = this.makeUpStopReelSet(false);
        const alreadyFilled = this._stopReelSetFilledBottomNum + unfinishedSpinningStackedSymbolTopFillUp.length;
        const needToFill = OktoberfestReelController.symbolsNumInData[4] + spinStopOffset;

        if (alreadyFilled < needToFill) {
            this._currentReelSet = this._currentReelSet.concat(this.makeUpStopReelSetRandomBottomFillUp(needToFill - alreadyFilled).concat(unfinishedSpinningStackedSymbolTopFillUp));
        } else {
            this._currentReelSet = this._currentReelSet.concat(unfinishedSpinningStackedSymbolTopFillUp);
            spinStopOffset = alreadyFilled - OktoberfestReelController.symbolsNumInData[4];
        }
        this._currentReelSetPosition = this._currentReelSet.length;
        this._stopCounter = this._symsNumTotal + spinStopOffset - 1;

        addDelayToNextReel += spinStopOffset * this._defaultSpinStepDuration;
        addDelayToNextReel += this.reelView.currentStepLeftTime;

        if (addDelayToNextReel > 0) {
            // Logger.logDev(`reel ${this.reelId} get ready to stop, add delay ${addDelayToNextReel} to next reels`);
            for (let i = this.reelId + 1; i < Oktoberfest.reels.length; i++) {
                Oktoberfest.reels[i]?.addStopDelayIfNotEnough(addDelayToNextReel);
            }
        }

        this.setNextState(ReelState.STOPPING);
    }

    private recycleBottomSymbol(state: OktoberfestSymbolState): OktoberfestSymbol {
        const newSymbol = this._removedSymbolPool.shift()!;
        newSymbol.getStateAnimation(state, {duration: 0}, {duration: 0});
        this.reelView.addSymbol(newSymbol);
        StackedSymbol.updateStackedSymbols(newSymbol, this.reelView.allSymbols[this.reelView.allSymbols.length - 1], 1, false);
        this.reelView.allSymbols.push(newSymbol);
        return newSymbol;
    }

    private revertTopSymbol(): void {
        this._currentReelSetPosition++;
        const removeSymbol = this.reelView.allSymbols.shift()!;
        // Don't need to review stacked state for the top symbol.
        this.reelView.removeSymbol(removeSymbol);
        removeSymbol.onRemove();
    }
}